<template>
  <div class="space-y-2">
    <div class="flex items-center justify-between gap-2">
      <div class="text-xs text-gray-500 dark:text-gray-400">
        <span>{{ tt('Select an MQTT property', 'Izberite lastnost MQTT') }}</span>
      </div>
      <button
        v-if="localConfig.propertyId"
        type="button"
        class="text-xs text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
        @click="clearSelection">
        {{ tt('Clear', 'Počisti') }}
      </button>
    </div>

    <select
      v-model="selectedPropertyId"
      class="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
      :disabled="loading"
      @change="onSelectionChange">
      <option value="">{{ tt('Select MQTT property...', 'Izberite lastnost MQTT...') }}</option>
      <option v-for="property in properties" :key="property.id" :value="property.id">
        {{ formatPropertyLabel(property) }}
      </option>
    </select>

    <div v-if="error" class="text-xs text-red-600 dark:text-red-400">
      {{ error }}
    </div>
    <div v-else-if="loading" class="text-xs text-gray-500 dark:text-gray-400">
      {{ tt('Loading MQTT properties...', 'Nalaganje lastnosti MQTT...') }}
    </div>
    <div v-else-if="!properties.length" class="text-xs text-gray-500 dark:text-gray-400 italic">
      {{ tt('No MQTT properties available.', 'Ni razpoložljivih lastnosti MQTT.') }}
    </div>

    <div v-if="localConfig.propertyId" class="grid grid-cols-2 gap-2 text-xs text-gray-600 dark:text-gray-300">
      <div>
        <span class="font-medium">{{ tt('Topic:', 'Tema:') }}</span>
        {{ localConfig.topicPath }}
      </div>
      <div>
        <span class="font-medium">{{ tt('Payload:', 'Vsebina:') }}</span>
        {{ localConfig.payloadType }}
      </div>
      <div v-if="localConfig.commandTopic">
        <span class="font-medium">{{ tt('Command:', 'Ukaz:') }}</span>
        {{ localConfig.commandTopic }}
      </div>
      <div v-if="localConfig.stateTopic">
        <span class="font-medium">{{ tt('State:', 'Stanje:') }}</span>
        {{ localConfig.stateTopic }}
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { onMounted, ref, watch } from 'vue';
import type { PropertyMapping } from '../shared/propertyMapping';
import { apiFetch } from '../services/api';
import { tt } from '../utils/consoleText';

interface MQTTPropertyOption {
  id: string;
  topicPath: string;
  propertyName?: string | null;
  logicalDeviceKey?: string | null;
  stateTopic?: string | null;
  commandTopic?: string | null;
  availabilityTopic?: string | null;
  payloadType: string;
  qos: number;
  retain: boolean;
}

export interface MQTTConfig {
  propertyId: string;
  topicPath: string;
  propertyName?: string;
  logicalDeviceKey?: string;
  stateTopic?: string | null;
  commandTopic?: string | null;
  availabilityTopic?: string | null;
  payloadType: string;
  qos: number;
  retain: boolean;
}

interface Props {
  modelValue?: MQTTConfig;
  mapping: PropertyMapping | undefined;
  integrationId: string;
}

const props = defineProps<Props>();
const emit = defineEmits<{
  'update:modelValue': [value: MQTTConfig];
}>();

const loading = ref(false);
const error = ref<string | null>(null);
const properties = ref<MQTTPropertyOption[]>([]);

const localConfig = ref<MQTTConfig>({
  propertyId: props.modelValue?.propertyId || '',
  topicPath: props.modelValue?.topicPath || '',
  propertyName: props.modelValue?.propertyName,
  logicalDeviceKey: props.modelValue?.logicalDeviceKey,
  stateTopic: props.modelValue?.stateTopic ?? null,
  commandTopic: props.modelValue?.commandTopic ?? null,
  availabilityTopic: props.modelValue?.availabilityTopic ?? null,
  payloadType: props.modelValue?.payloadType || 'string',
  qos: props.modelValue?.qos ?? 0,
  retain: props.modelValue?.retain ?? false,
});

const selectedPropertyId = ref(localConfig.value.propertyId);

watch(
  () => props.modelValue,
  (newValue) => {
    if (!newValue) {
      return;
    }

    localConfig.value = { ...newValue };
    selectedPropertyId.value = newValue.propertyId || '';
  },
  { deep: true },
);

watch(
  () => props.integrationId,
  async () => {
    await loadProperties();
  },
);

onMounted(async () => {
  await loadProperties();
});

function formatPropertyLabel(property: MQTTPropertyOption): string {
  const name = property.propertyName ? ` - ${property.propertyName}` : '';
  return `${property.topicPath}${name} (${property.payloadType})`;
}

function clearSelection() {
  localConfig.value = {
    propertyId: '',
    topicPath: '',
    propertyName: undefined,
    logicalDeviceKey: undefined,
    stateTopic: null,
    commandTopic: null,
    availabilityTopic: null,
    payloadType: 'string',
    qos: 0,
    retain: false,
  };
  selectedPropertyId.value = '';
  emit('update:modelValue', { ...localConfig.value });
}

function onSelectionChange() {
  const property = properties.value.find((item) => item.id === selectedPropertyId.value);

  if (!property) {
    clearSelection();
    return;
  }

  localConfig.value = {
    propertyId: property.id,
    topicPath: property.topicPath,
    propertyName: property.propertyName || undefined,
    logicalDeviceKey: property.logicalDeviceKey || undefined,
    stateTopic: property.stateTopic ?? null,
    commandTopic: property.commandTopic ?? null,
    availabilityTopic: property.availabilityTopic ?? null,
    payloadType: property.payloadType,
    qos: property.qos,
    retain: property.retain,
  };

  emit('update:modelValue', { ...localConfig.value });
}

async function loadProperties() {
  if (!props.integrationId) {
    properties.value = [];
    return;
  }

  loading.value = true;
  error.value = null;

  try {
    const data = await apiFetch(`/integrations/${props.integrationId}/datapoints`);
    properties.value = data.mqtt || [];
  } catch (err) {
    console.error('[MQTTReferenceInput] Failed to load properties', err);
    error.value =
      err instanceof Error
        ? err.message
        : tt('Failed to load MQTT properties', 'Lastnosti MQTT ni bilo mogoče naložiti');
  } finally {
    loading.value = false;
  }
}
</script>
